import { useEffect, useState } from 'react'
import { useLanguage } from '../i18n/LanguageProvider'
import { pluralKey } from '../i18n/plural'

// Google Maps embed — no API key, no JS to load
const GMAPS_SRC =
  'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2000!2d2.3463!3d48.8763!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e66e3d1b7e4b93%3A0x0!2s1+Bis+Rue+Riboutt%C3%A9%2C+75009+Paris!5e0!3m2!1sfr!2sfr!4v1'

const VELIB_URL =
  'https://opendata.paris.fr/api/explore/v2.1/catalog/datasets/velib-disponibilite-en-temps-reel/records?where=stationcode%3D%229009%22&limit=1'

interface VelibData {
  available_bikes: number
  ebike: number
  mechanical: number
  numdocksavailable: number
}

// ---------- Vélib' live widget ----------

function VelibWidget() {
  const { t, lang } = useLanguage()
  const [data, setData] = useState<VelibData | null>(null)
  const [error, setError] = useState(false)

  // `fetchVelib` vit DANS l'effet, et pas à côté, uniquement pour fermer sur
  // `cancelled`. Sans ce drapeau, la continuation d'une requête encore en vol
  // appelle setData/setError APRÈS le démontage : `clearInterval` seul arrête
  // les requêtes FUTURES, jamais celle qui est déjà partie. En test, jsdom est
  // alors détruit et `dispatchSetState` déréférence un `window` disparu —
  // l'erreur naît dans le `catch`, donc personne ne la rattrape, et la promesse
  // flottante de l'effet devient une Unhandled Rejection qui fait sortir vitest
  // en échec alors que tous les fichiers passent (vu en CI le 2026-10-01).
  useEffect(() => {
    let cancelled = false

    async function fetchVelib() {
      try {
        const res = await fetch(VELIB_URL)
        if (cancelled) return
        if (!res.ok) throw new Error('HTTP error')
        const json = await res.json()
        if (cancelled) return
        const record = json.results?.[0]
        if (!record) throw new Error('No data')
        setData({
          available_bikes: (record.numbikesavailable ?? 0),
          ebike: (record.ebike ?? 0),
          mechanical: (record.mechanical ?? 0),
          numdocksavailable: (record.numdocksavailable ?? 0),
        })
        setError(false)
      } catch {
        // Le garde est AUSSI là, et c'est le cas qui a cassé la CI : un rejet
        // après démontage passait par ici pour appeler `setError(true)`.
        if (cancelled) return
        setError(true)
      }
    }

    fetchVelib()
    const id = setInterval(fetchVelib, 60_000)
    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [])

  if (error || !data) {
    return (
      <a
        href="https://www.velib-metropole.fr/map?station=9009"
        target="_blank"
        rel="noopener noreferrer"
        className="text-accent hover:text-accent-hover text-sm underline"
      >
        {t('access.velib.link')}
      </a>
    )
  }

  // La forme plurielle vient d'ICU (plan 95-09), plus d'un ternaire sur le
  // compte. C'est le compteur du produit qui atteint vraiment zéro — station
  // vide — et les deux langues n'y sont pas d'accord : « 0 vélo dispo » au
  // singulier en français, « 0 bikes available » au pluriel en anglais.
  const bikes = t(pluralKey('access.velib.bikes', data.available_bikes, lang), {
    count: data.available_bikes,
    ebike: data.ebike,
    mechanical: data.mechanical,
  })
  const docks = t(pluralKey('access.velib.docks', data.numdocksavailable, lang), {
    count: data.numdocksavailable,
  })

  return (
    <p className="text-sm text-text-muted mt-2 flex items-center gap-2 flex-wrap">
      <span>
        {bikes} · {docks}
      </span>
      <span className="bg-accent/30 text-accent text-[10px] font-medium px-1.5 py-0.5 rounded">
        {t('access.velib.live')}
      </span>
    </p>
  )
}

// ---------- Google Maps minimap ----------

function Minimap() {
  const { t } = useLanguage()
  return (
    <iframe
      src={GMAPS_SRC}
      width="100%"
      height="100%"
      style={{ border: 0 }}
      allowFullScreen
      loading="lazy"
      referrerPolicy="no-referrer-when-downgrade"
      aria-label={t('access.map.aria')}
      title={t('access.map.title')}
    />
  )
}

// ---------- Main section ----------

export default function HowToGetHere() {
  const { t } = useLanguage()
  return (
    <section id="comment-venir" className="pt-20 pb-20 px-6">
      <div className="max-w-[1100px] mx-auto">
        {/* Header */}
        <span className="text-xs font-medium uppercase tracking-[0.15em] text-text-muted mb-4 block">
          {t('access.eyebrow')}
        </span>
        <h2 className="font-display font-[800] text-[clamp(1.8rem,4vw,2.5rem)] mb-2">
          {t('access.title')}
        </h2>
        {/* ponytail: l'adresse postale reste littérale — une rue parisienne ne
            se traduit pas, et une clé identique dans les deux catalogues est
            du bruit que la Phase 97 aurait à trier. */}
        <p className="text-text-muted mb-8">1 bis rue Riboutté, 75009 Paris</p>

        {/* Map + transport side by side */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">

          {/* Colonne gauche : carte */}
          <div className="aspect-square w-full rounded-[8px] overflow-hidden border border-border">
            <Minimap />
          </div>

          {/* Colonne droite : transport cards */}
          <div className="flex flex-col gap-4">
            {/* Métro */}
            <div className="bg-surface border border-border rounded-[8px] p-5">
              <h3 className="font-display font-bold text-base mb-3 flex items-center gap-2">
                <span aria-hidden="true">🚇</span> {t('access.metro.title')}
              </h3>
              <ul className="space-y-1.5 text-sm text-text-muted">
                <li>{t('access.metro.cadet')}</li>
                <li>{t('access.metro.grandsBoulevards')}</li>
                <li>{t('access.metro.notreDame')}</li>
              </ul>
            </div>

            {/* Bus */}
            <div className="bg-surface border border-border rounded-[8px] p-5">
              <h3 className="font-display font-bold text-base mb-3 flex items-center gap-2">
                <span aria-hidden="true">🚌</span> {t('access.bus.title')}
              </h3>
              <ul className="space-y-1.5 text-sm text-text-muted">
                <li>{t('access.bus.montholon')}</li>
                <li>{t('access.bus.cadet')}</li>
              </ul>
            </div>

            {/* Train / RER */}
            <div className="bg-surface border border-border rounded-[8px] p-5">
              <h3 className="font-display font-bold text-base mb-3 flex items-center gap-2">
                <span aria-hidden="true">🚆</span> {t('access.train.title')}
              </h3>
              <ul className="space-y-1.5 text-sm text-text-muted">
                <li>{t('access.train.nord')}</li>
                <li>{t('access.train.saintLazare')}</li>
              </ul>
            </div>

            {/* Vélib' */}
            <div className="bg-surface border border-border rounded-[8px] p-5">
              <h3 className="font-display font-bold text-base mb-3 flex items-center gap-2">
                <span aria-hidden="true">🚲</span> {t('access.velib.title')}
              </h3>
              <p className="text-sm text-text-muted">{t('access.velib.station')}</p>
              <p className="text-sm text-text-muted">{t('access.velib.walk')}</p>
              <VelibWidget />
            </div>

            {/* Parking */}
            <div className="bg-surface border border-border rounded-[8px] p-5">
              <h3 className="font-display font-bold text-base mb-3 flex items-center gap-2">
                <span aria-hidden="true">🅿️</span> {t('access.parking.title')}
              </h3>
              <p className="text-sm text-text-muted">{t('access.parking.name')}</p>
              <p className="text-sm text-text-muted">{t('access.parking.walk')}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
